import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, Package, Edit, Trash2, X, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MATERIAL_CATEGORIES = [
  'Cement', 'Steel', 'Sand', 'Aggregates', 'Bricks', 'Tiles',
  'Paint', 'Electrical', 'Plumbing', 'Hardware', 'Wood', 'Glass', 'Other'
];

const UNITS = ['kg', 'ton', 'bag', 'piece', 'sqft', 'cubic meter', 'liter', 'meter'];

const MaterialManagement = () => {
  const { getAuthHeader } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    unit: '',
    current_stock: 0,
    min_stock_level: 0,
    unit_price: 0
  });

  const fetchMaterials = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/erp/procurement/materials`, {
        headers: getAuthHeader()
      });
      setMaterials(res.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMaterial) {
        await axios.put(
          `${BACKEND_URL}/api/erp/procurement/materials/${editingMaterial.id}`,
          formData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/erp/procurement/materials`,
          formData,
          { headers: getAuthHeader() }
        );
      }
      fetchMaterials();
      setShowModal(false);
      setFormData({ name: '', category: '', unit: '', current_stock: 0, min_stock_level: 0, unit_price: 0 });
      setEditingMaterial(null);
    } catch (error) {
      console.error('Error saving material:', error);
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      category: material.category,
      unit: material.unit,
      current_stock: material.current_stock,
      min_stock_level: material.min_stock_level,
      unit_price: material.unit_price
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this material?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/erp/procurement/materials/${id}`, {
        headers: getAuthHeader()
      });
      fetchMaterials();
    } catch (error) {
      console.error('Error deleting material:', error);
    }
  };

  const lowStockMaterials = materials.filter(m => m.current_stock < m.min_stock_level);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Material Management</h1>
          <p className="text-gray-600">Track and manage construction materials</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-primary">
          <Plus className="w-4 h-4 mr-2" />
          Add Material
        </Button>
      </div>

      {lowStockMaterials.length > 0 && (
        <Card className="p-4 mb-6 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle size={20} />
            <span className="font-semibold">{lowStockMaterials.length} material(s) below minimum stock level</span>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {materials.map(material => (
          <Card key={material.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-lg">{material.name}</h3>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                  {material.category}
                </span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(material)}>
                  <Edit size={14} className="text-blue-600" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(material.id)}>
                  <Trash2 size={14} className="text-red-600" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Current Stock:</span>
                <span className={`font-bold ${material.current_stock < material.min_stock_level ? 'text-red-600' : 'text-green-600'}`}>
                  {material.current_stock} {material.unit}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Min Level:</span>
                <span className="font-medium">{material.min_stock_level} {material.unit}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Unit Price:</span>
                <span className="font-medium">₹{material.unit_price}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingMaterial ? 'Edit Material' : 'Add Material'}</h2>
              <Button size="sm" variant="ghost" onClick={() => { setShowModal(false); setEditingMaterial(null); }}>
                <X size={20} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Material Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Category</option>
                  {MATERIAL_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unit</label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({...formData, unit: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Unit</option>
                  {UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Current Stock</label>
                  <input
                    type="number"
                    value={formData.current_stock}
                    onChange={(e) => setFormData({...formData, current_stock: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Level</label>
                  <input
                    type="number"
                    value={formData.min_stock_level}
                    onChange={(e) => setFormData({...formData, min_stock_level: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unit Price (₹)</label>
                <input
                  type="number"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({...formData, unit_price: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingMaterial(null); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary">
                  {editingMaterial ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialManagement;